import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval, Subject } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  
  // Observable for unrecoverable state notifications (critical errors)
  public unrecoverableState$ = new Subject<string>();
  
  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private translate: TranslateService
  ) {
    if (this.swUpdate.isEnabled) {
      this.initUpdateChecks();
      this.listenForUpdates();
    }
  }

  /**
   * Initialize periodic update checks
   * Checks for updates every 6 hours and when app becomes stable
   */
  private initUpdateChecks(): void {
    // Wait for app to stabilize, then check for updates every 6 hours
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const every6Hours$ = interval(6 * 60 * 60 * 1000); // 6 hours in milliseconds
    const every6HoursOnceAppIsStable$ = concat(appIsStable$, every6Hours$);

    every6HoursOnceAppIsStable$.subscribe(() => {
      this.checkForUpdate();
    });
  }

  /**
   * Listen for available updates and prompt user
   */
  private listenForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(event => {
        console.log('New version available:', event.latestVersion);
        // Note: Update notification is handled by UpdateNotificationComponent
        // No need to prompt here - the component will show the UI
      });
  }

  /**
   * Manually check for updates
   */
  public async checkForUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      try {
        const updateAvailable = await this.swUpdate.checkForUpdate();
        console.log(updateAvailable ? 'Update available' : 'Already on latest version');
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }
  }

  /**
   * Activate the update and reload the page
   */
  public async activateUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      try {
        await this.swUpdate.activateUpdate();
        // Reload the page to load the new version
        document.location.reload();
      } catch (error) {
        console.error('Error activating update:', error);
      }
    }
  }

  /**
   * Listen for unrecoverable state (critical Service Worker errors)
   * Instead of using window.confirm, emit event for UI component to handle
   */
  public listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('App is in unrecoverable state:', event.reason);
      const message = this.translate.instant('PWA.UpdateRequired');
      
      // Emit event for UI component to show notification instead of using confirm()
      this.unrecoverableState$.next(message);
    });
  }
}
